/* eslint-disable @typescript-eslint/no-explicit-any */
import './office-modal.scss';

import { Box, Stack } from '@mui/material';
import {
  ContactType,
  UserGroup,
  getPaginatedNodes,
  useGetBDMRegion,
  useGetRegion,
  useGetUsers,
  useUpsertBrokerageMutation,
} from '@converge/graphql';
import { CreateOfficeField, getCreateOfficeFieldProps } from '@converge/data';
import {
  CreateOfficeFieldsSchema,
  CreateOfficeFieldsSchemaNoAddress,
} from './create.office.validation.schema';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import {
  IOffices,
  IPerson,
  primaryBrokerageAddress,
} from './accordion.form-container';
import {
  NewCountryFormSelectField,
  NewFormTextField,
  NewRegionFormSelectField,
  NewTypeAhead,
} from '../../presentation/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useConvergeNavigation, useConvergeSnackBar } from '../../contexts';

import { ApolloError } from '@apollo/client';
import { DialogLayout } from '../../presentation/components/dialog-layout';
import { NewConvergeButton } from '../../presentation/components/new-button';
import { config } from '@converge/config';
import { getUserOptions } from '../submission';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

interface AddOfficeModalProps {
  onClose: () => void;
  title: string;
  selectedOffice?: IOffices | null;
  primaryBrokerageAddress?: primaryBrokerageAddress;
  brokers: IPerson[];
}
//check
interface CreateOfficeFields {
  [CreateOfficeField.AddressUuid]: string;
  [CreateOfficeField.Name]: string;
  [CreateOfficeField.OfficeUuid]: string;
  [CreateOfficeField.UwTerritoryUuids]: string | undefined | never[];
  [CreateOfficeField.AppendUwTerritories]: boolean;
  [CreateOfficeField.Street1]: string;
  [CreateOfficeField.Street2]: string;
  [CreateOfficeField.City]: string;
  [CreateOfficeField.RegionUuid]: string;
  [CreateOfficeField.CountryUuid]: string;
  [CreateOfficeField.PostalCode]: string;
  [CreateOfficeField.useCorpAddress]: boolean | undefined | null;
  [CreateOfficeField.PrimaryContactEmail]: string | undefined | null;
  [CreateOfficeField.PrimaryContactUuid]: string | undefined | null;
  [CreateOfficeField.BusinessDevelopmentManager]: undefined | null | string;
  [CreateOfficeField.Underwriter]: undefined | null | string;
  [CreateOfficeField.BdmTerritoryUuids]: string | undefined | never[];
}

export const AddOfficeModal = ({
  onClose,
  title,
  selectedOffice,
  primaryBrokerageAddress,
  brokers,
}: AddOfficeModalProps) => {
  const [isCorporateAddress, setIsCorporateAddress] = useState<boolean>(
    selectedOffice ? selectedOffice?.node?.useCorpAddress ?? false : true
  );
  const formMethods = useForm<CreateOfficeFields>({
    resolver: yupResolver(
      isCorporateAddress
        ? CreateOfficeFieldsSchemaNoAddress
        : CreateOfficeFieldsSchema
    ),
    defaultValues: {
      [CreateOfficeField.AddressUuid]: '',
      [CreateOfficeField.Name]: '',
      [CreateOfficeField.OfficeUuid]: '',
      [CreateOfficeField.UwTerritoryUuids]: '',
    },
  });
  const { data: region, loading: isUwRegionLoading } = useGetRegion();
  const { data: bdmRegion, loading: isBdmRegionLoading } = useGetBDMRegion();

  const UNDERWRITING_REGION_OPTIONS = region?.uwTerritories?.edges
    ?.map((parentOption) => {
      return {
        value: parentOption?.node?.uuid || '',
        label: parentOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const BDM_REGION_OPTIONS = bdmRegion?.bdmTerritories?.edges
    ?.map((bdmRegionOption) => {
      return {
        value: bdmRegionOption?.node?.uuid || '',
        label: bdmRegionOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const { brokerageUuid } = useParams();
  const navigate = useConvergeNavigation();
  const { notify } = useConvergeSnackBar();
  const { loading, upsertEntity } = useUpsertBrokerageMutation();
  const { data: underwriterList } = useGetUsers({
    userGroups: [UserGroup.Underwriter],
  });
  const { data: businessDevManager } = useGetUsers({
    userGroups: [UserGroup.Bdm],
  });

  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) ?? [],
    [underwriterList]
  );

  const BUSINESS_DEV_MANAGER_OPTIONS = useMemo(
    () => getUserOptions(businessDevManager) ?? [],
    [businessDevManager]
  );

  const brokerList = brokers?.map((broker) => ({
    label: `${broker.node.firstName} ${broker.node.lastName}`,
    value: broker.node.uuid,
    data: broker.node.contacts?.edges
      .filter((item) => item?.node?.type === ContactType.Email)
      ?.map((email) => ({
        label: email.node.value,
        value: email.node.uuid,
      })),
  }));
  const officeContact = getPaginatedNodes(selectedOffice?.node?.persons).filter(
    (contactInfo) => contactInfo.primaryContact
  );
  const contacts = officeContact[0]?.contacts?.edges[0]?.node?.value;

  const BusinessDevelopmentManagerUuid =
    selectedOffice?.node?.users?.edges?.filter(
      (bDMuser) => bDMuser?.node?.type === UserGroup.Bdm
    )[0]?.node?.uuid;

  const underwriterUuid = selectedOffice?.node?.users?.edges?.filter(
    (underwriter) => underwriter?.node?.type === UserGroup.Underwriter
  )[0]?.node?.uuid;
  useEffect(() => {
    if (selectedOffice) {
      const uwTerritories =
        selectedOffice?.node?.uwTerritories?.edges[0]?.node?.uuid ?? [];
      const bdmTerritories =
        selectedOffice?.node?.bdmTerritories?.edges[0]?.node?.uuid ?? [];
      formMethods.setValue(
        CreateOfficeField.Name,
        formMethods.getValues(CreateOfficeField.Name)?.length === 0
          ? selectedOffice?.node?.name ?? ''
          : formMethods.getValues(CreateOfficeField.Name) ?? ''
      );
      formMethods.setValue(
        CreateOfficeField.OfficeUuid,
        selectedOffice?.node?.uuid ?? ''
      );

      formMethods.setValue(
        CreateOfficeField.PrimaryContactUuid,
        officeContact[0]?.uuid
      );
      formMethods.setValue(
        CreateOfficeField.PrimaryContactEmail,
        contacts ?? ''
      );
      formMethods.setValue(
        CreateOfficeField.BusinessDevelopmentManager,
        BusinessDevelopmentManagerUuid || ''
      );

      formMethods.setValue(
        CreateOfficeField.Underwriter,
        underwriterUuid || ''
      );

      formMethods.setValue(
        CreateOfficeField.UwTerritoryUuids,
        uwTerritories || []
      );
      formMethods.setValue(
        CreateOfficeField.BdmTerritoryUuids,
        bdmTerritories || ''
      );

      if (selectedOffice?.node?.address) {
        /* eslint-disable no-unsafe-optional-chaining */
        const { street1, street2, city, regions, countries, postalCode } =
          selectedOffice?.node?.address;
        if (street1) {
          formMethods.setValue(
            CreateOfficeField.Street1,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryAddress1 ?? ''
              : street1
          );
        }
        if (street2) {
          formMethods.setValue(
            CreateOfficeField.Street2,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryAddress2 ?? ''
              : street2
          );
        }
        if (city) {
          formMethods.setValue(
            CreateOfficeField.City,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryCity ?? ''
              : city
          );
        }
        if (postalCode) {
          formMethods.setValue(
            CreateOfficeField.PostalCode,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryPostalCode ?? ''
              : postalCode
          );
        }
        if (regions?.edges?.length > 0 && regions?.edges[0]?.node?.uuid) {
          formMethods.setValue(
            CreateOfficeField.RegionUuid,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryRegion ?? ''
              : regions?.edges[0]?.node?.uuid
          );
        }
        if (countries?.edges?.length > 0) {
          formMethods.setValue(
            CreateOfficeField.CountryUuid,
            isCorporateAddress
              ? primaryBrokerageAddress?.primaryCountry ?? ''
              : countries.edges[0]?.node?.uuid
          );
        }
      }
    } else {
      formMethods.setValue(
        CreateOfficeField.Name,
        formMethods.getValues(CreateOfficeField.Name)
      );
      formMethods.setValue(CreateOfficeField.OfficeUuid, '');
      formMethods.setValue(
        CreateOfficeField.UwTerritoryUuids,
        formMethods.getValues(CreateOfficeField.UwTerritoryUuids)
      );
      formMethods.setValue(
        CreateOfficeField.BdmTerritoryUuids,
        formMethods.getValues(CreateOfficeField.BdmTerritoryUuids)
      );
      formMethods.setValue(
        CreateOfficeField.Street1,
        isCorporateAddress ? primaryBrokerageAddress?.primaryAddress1 ?? '' : ''
      );
      formMethods.setValue(
        CreateOfficeField.Street2,
        isCorporateAddress
          ? primaryBrokerageAddress?.primaryAddress2 &&
            primaryBrokerageAddress?.primaryAddress2.length > 0
            ? primaryBrokerageAddress?.primaryAddress2
            : '-'
          : ''
      );
      formMethods.setValue(
        CreateOfficeField.City,
        isCorporateAddress ? primaryBrokerageAddress?.primaryCity ?? '' : ''
      );
      formMethods.setValue(
        CreateOfficeField.PostalCode,
        isCorporateAddress
          ? primaryBrokerageAddress?.primaryPostalCode ?? ''
          : ''
      );
      formMethods.setValue(
        CreateOfficeField.RegionUuid,
        isCorporateAddress ? primaryBrokerageAddress?.primaryRegion ?? '' : ''
      );
      formMethods.setValue(
        CreateOfficeField.CountryUuid,
        isCorporateAddress
          ? primaryBrokerageAddress?.primaryCountry ??
              'bb3ca70e-1df7-40a5-966e-f82ebc541cd3'
          : 'bb3ca70e-1df7-40a5-966e-f82ebc541cd3'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMethods, selectedOffice, isCorporateAddress]);

  const SubmitLicenseHandler = useCallback(
    (): SubmitHandler<CreateOfficeFields> => async (fields) => {
      const getOfficeData = (fields: CreateOfficeFields) => {
        const selectedOfficeUuid = selectedOffice?.node?.uuid;

        return {
          name: fields.name,
          officeUuid: selectedOfficeUuid ?? null,
          uwTerritoryUuids:
            fields?.uwTerritoryUuids && fields?.uwTerritoryUuids?.length > 0
              ? [fields.uwTerritoryUuids]
              : [],
          bdmTerritoryUuids:
            fields?.bdmTerritoryUuids && fields?.bdmTerritoryUuids?.length > 0
              ? [fields.bdmTerritoryUuids]
              : [],

          useCorpAddress: isCorporateAddress,
          bdmUserUuid: fields?.bdmUserUuid ? fields?.bdmUserUuid : 'null',
          underwriterUserUuid: fields?.underwriterUserUuid
            ? fields?.underwriterUserUuid
            : 'null',

          primaryPersonUuid: fields?.primaryPersonUuid
            ? fields?.primaryPersonUuid
            : 'null',
        };
      };

      let LicenseResponse, LicenseResponseErrors;
      const selectedAddressUuid = selectedOffice?.node?.address?.uuid;

      const isSameAsCorporateAddress =
        selectedAddressUuid === primaryBrokerageAddress?.primaryAddressUuid;
      const officeData = {
        entityUuid: brokerageUuid,
        offices: [getOfficeData(fields)],
      } as any;

      const officeAddressData = {
        entityUuid: brokerageUuid,
        addresses: [
          {
            addressUuid:
              selectedAddressUuid && !isSameAsCorporateAddress
                ? selectedAddressUuid
                : null,
            street1: fields.street1,
            street2: fields.street2,
            city: fields.city,
            regionUuid: fields.regionUuid,
            countryUuid: fields.countryUuid,
            postalCode: fields.postalCode,
            officeDetails: getOfficeData(fields),
          },
        ],
      } as any;

      try {
        const { data, errors } = await upsertEntity({
          variables: {
            entityInput: isCorporateAddress ? officeData : officeAddressData,
          },
        });
        LicenseResponse = data;
        LicenseResponseErrors = errors;

        notify(
          `Brokerage office has been  ${
            selectedOffice?.node?.uuid ? 'updated' : 'added'
          }`,
          {
            variant: 'success',
          }
        );
        onClose();
        navigate.brokerage({ brokerageUuid: brokerageUuid });
      } catch (cause) {
        console.error(
          `Failed to ${
            selectedOffice?.node?.uuid ? 'update' : 'save'
          } office. Please try again. (${cause})`
        );
        if (cause instanceof ApolloError) {
          LicenseResponseErrors = cause.graphQLErrors;
        }
      }
      const errorMessage = LicenseResponseErrors?.[0]?.message;
      if (errorMessage || !LicenseResponse) {
        notify(
          `Failed to ${
            selectedOffice?.node?.uuid ? 'update' : 'save'
          }office. Please try again: (${
            errorMessage || 'No data returned by the API'
          })`,
          {
            variant: 'error',
          }
        );
        return;
      }
    },
    [
      selectedOffice?.node?.address?.uuid,
      selectedOffice?.node?.uuid,
      primaryBrokerageAddress?.primaryAddressUuid,
      brokerageUuid,
      isCorporateAddress,
      upsertEntity,
      notify,
      onClose,
      navigate,
    ]
  );
  return (
    <DialogLayout
      sx={{
        overflow: 'hidden',
        '& .MuiPaper-root.MuiDialog-paper': {
          maxWidth: '100px',
          minWidth: '1200px',
          minHeight: '730px',

          overflow: 'hidden !important',
          fontFamily: 'Red Hat Text, Arial, sans-serif',
        },
        '& .MuiDialogContent-root ': {
          overflow: 'hidden !important',
        },
      }}
      open
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      className="add-office-modal"
      header={<Box className="modal-title-office"> {title}</Box>}
    >
      <FormProvider {...formMethods}>
        <Stack className="row-container">
          <Box className="fields-left">
            <NewFormTextField
              {...getCreateOfficeFieldProps(CreateOfficeField.Name)}
              placeholder="Enter Office Name"
              required
              dataTestId="office-name-input"
            />
          </Box>
          <Box className="fields-middle">
            <NewTypeAhead
              {...getCreateOfficeFieldProps(
                CreateOfficeField.PrimaryContactUuid
              )}
              options={brokerList || []}
              placeholder="Select Contact"
              dataTestId="office-primary-contact-dropdown"
              onChangeOption={(item) => {
                if (Array.isArray(item?.data) && item?.data?.length > 0) {
                  formMethods.setValue(
                    CreateOfficeField.PrimaryContactEmail,
                    item?.data[0]?.label ?? ''
                  );
                } else {
                  formMethods.setValue(
                    CreateOfficeField.PrimaryContactEmail,
                    ''
                  );
                }
              }}
            />
          </Box>
          <Box className="fields-right">
            <NewFormTextField
              {...getCreateOfficeFieldProps(
                CreateOfficeField.PrimaryContactEmail
              )}
              placeholder="name@domain.com"
              disabled={true}
              dataTestId="office-primary-contact-email-input"
            />
          </Box>
        </Stack>
        <Stack className="row-container">
          <Box className="fields-second-row-left-office">
            <NewTypeAhead
              {...getCreateOfficeFieldProps(
                CreateOfficeField.BusinessDevelopmentManager
              )}
              placeholder="Select BDM"
              options={BUSINESS_DEV_MANAGER_OPTIONS || []}
              dataTestId="office-bdm-dropdown"
            />
          </Box>
          <Box className="fields-second-row-first-middle-office">
            <NewTypeAhead
              {...getCreateOfficeFieldProps(
                CreateOfficeField.BdmTerritoryUuids
              )}
              options={BDM_REGION_OPTIONS || []}
              placeholder="Select BDM Region"
              isLoading={isBdmRegionLoading}
              dataTestId="office-bdm-region-dropdown"
            />
          </Box>

          <Box className="fields-second-row-middle-office">
            <NewTypeAhead
              {...getCreateOfficeFieldProps(CreateOfficeField.Underwriter)}
              placeholder="Select UW"
              options={UNDERWRITER_OPTIONS || []}
              dataTestId="office-underwriter-dropdown"
            />
          </Box>
          <Box className="fields-second-row-right-office  typeName">
            <NewTypeAhead
              {...getCreateOfficeFieldProps(CreateOfficeField.UwTerritoryUuids)}
              options={UNDERWRITING_REGION_OPTIONS}
              isLoading={isUwRegionLoading}
              placeholder="Select Region"
              dataTestId="office-uw-region-dropdown"
            />
          </Box>
        </Stack>
        <Stack className="same-billing-office  row-container">
          <Box
            data-testid="office-same-as-brokerage-address-checkbox"
            onClick={() => setIsCorporateAddress((prev) => !prev)}
            style={{ cursor: 'pointer', display: 'inline-flex' }}
          >
            {isCorporateAddress && (
              <img
                data-testid="office-same-as-brokerage-address-checkbox-checked"
                width="20px"
                src={config.checkBox}
                alt="checkBox"
                className="checkbox"
              />
            )}
            {!isCorporateAddress && (
              <img
                data-testid="office-same-as-brokerage-address-checkbox-unchecked"
                width="20px"
                src={config.unselectedCheckBox}
                alt="checkBox"
                className="checkbox"
              />
            )}
          </Box>
          <Box className="text">
            Office address same as brokerage primary address
          </Box>
        </Stack>

        <Stack className="address-container">
          <>
            <Box className="address-field">
              <NewFormTextField
                {...getCreateOfficeFieldProps(CreateOfficeField.Street1)}
                required
                autoComplete={false}
                placeholder="1234 Main St"
                dataTestId="office-street1-input"
                disabled={isCorporateAddress}
              />
            </Box>
            <Box className="address-field">
              <NewFormTextField
                {...getCreateOfficeFieldProps(CreateOfficeField.Street2)}
                autoComplete={false}
                placeholder="Suite 123"
                dataTestId="office-street2-input"
                disabled={isCorporateAddress}
              />
            </Box>
            <Box className="address-field">
              <NewFormTextField
                {...getCreateOfficeFieldProps(CreateOfficeField.City)}
                autoComplete={false}
                required
                placeholder="City"
                dataTestId="office-city-input"
                disabled={isCorporateAddress}
              />
            </Box>
          </>
        </Stack>
        <Stack className="address-container">
          <>
            <Box className="address-field">
              <NewRegionFormSelectField
                {...getCreateOfficeFieldProps(CreateOfficeField.RegionUuid)}
                required
                dataTestId="office-state-dropdown"
                disabled={isCorporateAddress}
              />
            </Box>
            <Box className="address-field">
              <NewFormTextField
                {...getCreateOfficeFieldProps(CreateOfficeField.PostalCode)}
                autoComplete={false}
                required
                placeholder="Enter Postal or ZIP Code"
                dataTestId="office-postal-code-input"
                disabled={isCorporateAddress}
              />
            </Box>
            <Box className="address-field">
              <NewCountryFormSelectField
                {...getCreateOfficeFieldProps(CreateOfficeField.CountryUuid)}
                dataTestId="office-country-dropdown"
                required
                disabled={true}
              />
            </Box>
          </>
        </Stack>

        <Stack className="button-container-office">
          <Box className="close-btn-Container">
            <NewConvergeButton
              label="CANCEL"
              variant="outlined"
              onClick={onClose}
              data-testid="cancel-office-button"
            />
          </Box>
          <Box>
            <NewConvergeButton
              onClick={formMethods.handleSubmit(SubmitLicenseHandler())}
              data-testid="submit-office-button"
              label={`${selectedOffice ? 'UPDATE' : 'ADD'} OFFICE`}
              loading={loading}
            />
          </Box>
        </Stack>
      </FormProvider>
    </DialogLayout>
  );
};
